import axios from "axios";
import { getAuthTokens, getRefreshToken, setAuthTokens } from "@/lib/authTokens";
import { PANEL_API_BASE } from "@/lib/panelConfig";

export const panelApi = axios.create({
  baseURL: PANEL_API_BASE,
  headers: { Accept: "application/json" },
});

panelApi.interceptors.request.use((config) => {
  const { access } = getAuthTokens();
  if (access) {
    config.headers.Authorization = `Bearer ${access}`;
  }
  if (config.data instanceof FormData) {
    delete config.headers["Content-Type"];
  } else if (!config.headers["Content-Type"]) {
    config.headers["Content-Type"] = "application/json";
  }
  return config;
});

let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  const refresh = getRefreshToken();
  if (!refresh) return null;
  const { data } = await axios.post<{ access: string }>(`${PANEL_API_BASE}/auth/refresh/`, {
    refresh,
  });
  const { refresh: storedRefresh } = getAuthTokens();
  setAuthTokens(data.access, storedRefresh);
  return data.access;
}

panelApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    if (error.response?.status === 401 && getRefreshToken() && !original._retry) {
      original._retry = true;
      refreshPromise ??= refreshAccessToken().finally(() => {
        refreshPromise = null;
      });
      const newAccess = await refreshPromise;
      if (newAccess) {
        original.headers.Authorization = `Bearer ${newAccess}`;
        return panelApi(original);
      }
    }
    return Promise.reject(error);
  },
);

export function setPanelTokens(access: string | null, refresh: string | null) {
  setAuthTokens(access, refresh);
}

export function getPanelTokens() {
  return getAuthTokens();
}

export function getPanelErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as Record<string, unknown> | undefined;
    if (typeof data?.detail === "string") return data.detail;
    if (data && typeof data === "object" && !Array.isArray(data)) {
      const labels: Record<string, string> = {
        title: "Título",
        category_id: "Categoria",
        summary: "Linha fina",
        body: "Corpo",
        status: "Status",
      };
      const messages: string[] = [];
      for (const [key, value] of Object.entries(data)) {
        if (key === "detail") continue;
        const label = labels[key] ?? key;
        if (Array.isArray(value) && typeof value[0] === "string") {
          messages.push(`${label}: ${value[0]}`);
        } else if (typeof value === "string") {
          messages.push(`${label}: ${value}`);
        }
      }
      if (messages.length) return messages.join(" ");
    }
    if (error.message) return error.message;
  }
  return "Erro inesperado.";
}
